import { Label } from "@/components/atoms/texts/label";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, Info, Plus, Trash2, X } from "lucide-react";

import useConsultant from "../../../../organisms/navigation/MenuConsultant/useConsultant";
import { useConsultantContext } from "@/context/ConsultantContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/organisms/forms/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/molecules/buttons/button";
import PrintInformationMessage from "@/components/organisms/feedback/PrintInformationMessage";
import PrintWarningMessage from "@/components/organisms/feedback/PrintWarningMessagee";
import PrintErrorMessage from "@/components/organisms/feedback/PrintErrorMessage";

const formatName = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(" ");

const FieldLabel = ({ message, required = false }) => (
    <span className="inline-flex items-center gap-0.5 grow">
        <Label size="small"  message={message} />
        {required && <Label size="small" color="error">*</Label>}
    </span>
);

export default function NewCooptation({
    global = false,
    showErrorMessage,
    cooptingUUID,
    changeCoopted,
    rows,
    addRow,
    updateRow,
    removeRow,
    removeAllRows,
    totalPercentage,
    cooptations,
    cooptationsLoading,
    cooptationsError,
}) {
    const [t] = useTranslation("global");
    const { usersFormated: users } = useConsultant();
    const { activeUser } = useConsultantContext();

    useEffect(() => {
        if (!global && activeUser?.uuid) {
            changeCoopted(activeUser.uuid);
        }
    }, [activeUser, global, changeCoopted]);

    const cooptantOptions = useMemo(() => {
        return (users || []).filter((user) => user.uuid !== cooptingUUID);
    }, [users, cooptingUUID]);

    const isCooptationsReady = Boolean(cooptingUUID) && cooptations?.uuid === cooptingUUID;
    const cooptedName = isCooptationsReady ? formatName(cooptations) : "";
    const hasCurrentCooptants = isCooptationsReady && (cooptations.cooptedBy || []).length > 0;
    const willDeleteAll = hasCurrentCooptants && rows.length === 0;
    const isTotalValid = totalPercentage === 100;

    if (!users) return <></>;

    return (
        <div className="flex flex-col gap-secondDegree p-secondDegree w-full">
            <div className="flex flex-col gap-firstDegree">
                <Label message="consultants.dashboard.newCooptation.newConsultantName" fontWeight="bold" />
                <Select value={cooptingUUID || ""} onValueChange={changeCoopted} disabled={!global}>
                    <SelectTrigger>
                        <SelectValue placeholder={<Label message="global.actions.select" />} />
                    </SelectTrigger>
                    <SelectContent>
                        {global
                            ? users.map((user) => (
                                <SelectItem key={user.uuid} value={user.uuid}>{user.trigram + " " + user.firstname + " " + user.lastname}</SelectItem>
                            ))
                            : activeUser?.uuid &&
                            <SelectItem value={activeUser.uuid}>{activeUser.trigram + " " + activeUser.firstname + " " + activeUser.lastname}</SelectItem>
                        }
                    </SelectContent>
                </Select>
            </div>

            {!cooptingUUID && (
                <PrintInformationMessage information="consultants.dashboard.newCooptation.selectCoopted" />
            )}

            {cooptingUUID && cooptationsLoading && !isCooptationsReady && <span className="loader" />}
            {cooptingUUID && cooptationsError && <PrintErrorMessage error={cooptationsError} />}

            {isCooptationsReady && (
                <>
                    <PrintInformationMessage
                        isComponent
                        information={(
                            <div className="flex gap-firstDegree">
                                <Info className="h-4 w-4 shrink-0 mt-0.5 text-information" />
                                <Label>{t("consultants.dashboard.newCooptation.description", { name: cooptedName })}</Label>
                            </div>
                        )}
                    />

                    <div className="flex flex-col gap-firstDegree">
                        <div className="flex items-center justify-between gap-firstDegree">
                            <Label fontWeight="bold">
                                {t("consultants.dashboard.newCooptation.cooptedBy")} ({rows.length})
                            </Label>
                            <div className="flex items-center gap-1">
                                {rows.length > 0 && (
                                    <span
                                        className={[
                                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5",
                                            isTotalValid ? "border-success text-success" : "border-error text-error",
                                        ].join(" ")}
                                    >
                                        {isTotalValid ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                                        <Label size="small" fontWeight="semibold">
                                            {t("consultants.dashboard.newCooptation.totalOwnership", { total: totalPercentage })}
                                        </Label>
                                    </span>
                                )}
                                <Button
                                    style="ghost"
                                    sizePadding="small"
                                    showLeftIcon
                                    leftIcon={<Trash2 className="h-4 w-4" />}
                                    showText={false}
                                    showToolTip
                                    text="consultants.dashboard.newCooptation.removeAllCooptants"
                                    onClick={removeAllRows}
                                    disabled={rows.length === 0}
                                />
                            </div>
                        </div>

                        {rows.map((row, index) => (
                            <div key={row.key} className="flex flex-col gap-firstDegree border-b border-medium pb-firstDegree">
                                <div className="flex items-end gap-1">
                                    <div className="flex flex-col gap-1 grow min-w-0">
                                        <FieldLabel message="consultants.dashboard.newCooptation.table.name" required />
                                        <Select value={row.uuid || undefined} onValueChange={(value) => updateRow(index, { uuid: value })}>
                                            <SelectTrigger className="bg-medium border-0">
                                                <SelectValue placeholder={<Label message="global.actions.select" />} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {cooptantOptions.map((user) => (
                                                    <SelectItem key={user.uuid} value={user.uuid}>
                                                        {user.trigram + " - " + user.firstname + " " + user.lastname}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        style="ghost"
                                        sizePadding="firstDegree"
                                        showLeftIcon
                                        leftIcon={<X className="h-4 w-4" />}
                                        showText={false}
                                        showToolTip
                                        text="consultants.dashboard.newCooptation.removeCooptant"
                                        onClick={() => removeRow(index)}
                                    />
                                </div>

                                <div className="grid grid-cols-[1fr_1fr_1.6fr] gap-firstDegree">
                                    <div className="flex flex-col gap-1">
                                        <FieldLabel message="consultants.dashboard.newCooptation.table.ownership" required />
                                        <Input
                                            value={row.pourcent}
                                            onChange={(e) => updateRow(index, { pourcent: e.target.value })}
                                            type="number"
                                            onWheel={(e) => e.target.blur()}
                                            placeholder="0"
                                            // className="bg-medium rounded-sm border-0 text-center h-9"
                                            className="text-center h-10"
                                            min="0"
                                            step="10"
                                            max="100"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <FieldLabel message="consultants.dashboard.newCooptation.table.duration" />
                                        <Input
                                            value={row.duration}
                                            onChange={(e) => updateRow(index, { duration: e.target.value })}
                                            type="number"
                                            onWheel={(e) => e.target.blur()}
                                            placeholder="∞"
                                            // className="bg-medium rounded-sm border-0 text-center h-9"
                                                                                        className="text-center h-10"

                                            min="0"
                                            step="6"
                                            max="120"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <FieldLabel message="consultants.dashboard.newCooptation.table.date" required />
                                        <Input
                                            value={row.date}
                                            onChange={(e) => updateRow(index, { date: e.target.value })}
                                            type="date"
                                            // className="bg-medium rounded-sm border-0 h-9"
                                                                                        className="text-center h-10"

                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        {rows.length === 0 && !willDeleteAll && (
                            <Label color="grey" message="consultants.dashboard.newCooptation.noCooptant" />
                        )}

                        <Button
                            style="dressedGhost"
                            className="w-full text-primary"
                            showLeftIcon
                            leftIcon={<Plus className="h-4 w-4" />}
                            text="consultants.dashboard.newCooptation.addCooptant"
                            onClick={addRow}
                        />
                    </div>

                    {willDeleteAll && (
                        <PrintWarningMessage
                            isComponent
                            warning={<Label>{t("consultants.dashboard.newCooptation.deleteAllWarning", { name: cooptedName })}</Label>}
                        />
                    )}
                </>
            )}

            {showErrorMessage && (
                <Label color="error" message={`error.${showErrorMessage}`} />
            )}
        </div>
    )
}
